import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from '@aimsos/i18n';
import { useToast } from '@aimsos/ui';

import { runTaskMutation } from '../../../services/tasks.js';

/**
 * Take over, Complete, Not now, Dismiss on a task row (CT-5722).
 *
 * One request per row at a time: `submittingRef` holds the rows in flight, so a
 * double click is refused before any state update lands. On success: a toast and
 * `onDone` (a quiet refetch — the server says where the row is now). On failure:
 * an error toast; the row is untouched.
 *
 * The Tasks tab stays mounted across records, and task ids repeat across them,
 * so a row in flight is keyed by its record: the same id on the next record is
 * neither busy nor refused (`isRunning`). An action that answers after the user
 * moved on still toasts — it did happen, on its record — but its follow-up does
 * not run on the record open now.
 */
export function useTaskAction({ entityType, entityId, onDone } = {}) {
  const { t } = useTranslation('unified-profiles');
  const toast = useToast();
  const [runningKeys, setRunningKeys] = useState(() => new Set());
  const submittingRef = useRef(new Set());
  const recordKey = `${entityType}/${entityId}`;
  const openRecordRef = useRef(recordKey);
  useEffect(() => {
    openRecordRef.current = recordKey;
  }, [recordKey]);

  const setRunning = useCallback((runKey, running) => {
    setRunningKeys((current) => {
      const next = new Set(current);
      if (running) next.add(runKey);
      else next.delete(runKey);
      return next;
    });
  }, []);

  const run = useCallback(
    async (task, mutation) => {
      const runKey = `${recordKey}/${task.id}`;
      if (submittingRef.current.has(runKey)) return false;
      submittingRef.current.add(runKey);
      setRunning(runKey, true);

      try {
        await runTaskMutation({ entityType, entityId, taskId: task.id, mutation });
        toast.success({
          title: t(`tasks.toast.${mutation}Title`),
          message: t(`tasks.toast.${mutation}Message`, { title: task.title }),
        });
        if (openRecordRef.current === recordKey) onDone?.();
        return true;
      } catch (err) {
        toast.error({ title: t('tasks.toast.errorTitle'), message: err?.message ?? t('tasks.toast.errorMessage') });
        return false;
      } finally {
        submittingRef.current.delete(runKey);
        setRunning(runKey, false);
      }
    },
    [recordKey, entityType, entityId, onDone, setRunning, toast, t],
  );

  const isRunning = useCallback((taskId) => runningKeys.has(`${recordKey}/${taskId}`), [runningKeys, recordKey]);

  return useMemo(() => ({ run, isRunning }), [run, isRunning]);
}

export default useTaskAction;
